import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  RefreshCw,
  TrendingUp,
  Package,
  Users,
  Heart,
  ShoppingCart,
  Sparkles,
  X
} from "lucide-react";
import { useOrganizationStore } from "@/store/useOrganizationStore";

interface InsightsInstallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InsightsInstallModal({
  open,
  onOpenChange
}: InsightsInstallModalProps) {
  const organization = useOrganizationStore((state) => state.organization);
  const navigate = useNavigate();

  const handleInstall = () => {
    window.open(
      `https://clover.com/appmarket/m/${organization.referenceId}/apps/${organization.appReferenceId}`,
      "_blank"
    );
  };

  const handleClose = () => {
    onOpenChange(false);
    navigate("/");
  };

  const handleMaybeLater = () => {
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        } else {
          onOpenChange(isOpen);
        }
      }}
    >
      <DialogContent className="max-w-3xl p-0 overflow-hidden [&>button]:hidden rounded-lg border-0 gap-0 shadow-xl">
        {/* Header with Teal Background */}
        <div className="bg-teal-600 text-white px-4 pt-4 pb-4 relative rounded-t-lg">
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-teal-600 z-10"
          >
            <X className="h-4 w-4 text-white" />
            <span className="sr-only">Close</span>
          </button>
          <div className="flex items-start gap-3 pr-7">
            <BarChart3 className="h-8 w-8 flex-shrink-0 text-white" />
            <div>
              <DialogTitle className="text-xl font-bold text-white mb-1 !mt-0">
                Unlock Smarter Insights for Your Business
              </DialogTitle>
              <DialogDescription className="text-white/90 text-sm !mt-0">
                Visual reports that help you understand performance and
                customers easily.
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Content with Two Columns */}
        <div className="p-4 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sales Reports Column */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-4 w-4 text-gray-700" />
                <h3 className="text-base font-semibold text-gray-900">
                  Sales Reports
                </h3>
              </div>

              {/* Overview Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <RefreshCw className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Overview
                    </h4>
                    <p className="text-xs text-gray-500">
                      See overall performance instantly
                    </p>
                  </div>
                </div>
              </div>

              {/* Trends & Patterns Card */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <TrendingUp className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Trends & Patterns
                    </h4>
                    <p className="text-xs text-gray-500">
                      Spot trends and business shifts
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Insights Card */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <Package className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Product Insights
                    </h4>
                    <p className="text-xs text-gray-500">
                      Know what sells and why
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Reports Column */}
            <div className="space-y-2.5">
              <div className="flex items-center gap-2 mb-3">
                <Users className="h-4 w-4 text-gray-700" />
                <h3 className="text-base font-semibold text-gray-900">
                  Customer Reports
                </h3>
              </div>

              {/* Overview Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <Users className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Overview
                    </h4>
                    <p className="text-xs text-gray-500">
                      Understand your customer base
                    </p>
                  </div>
                </div>
              </div>

              {/* Behavior & Engagement Card */}
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <Heart className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Behavior & Engagement
                    </h4>
                    <p className="text-xs text-gray-500">
                      See how customers interact
                    </p>
                  </div>
                </div>
              </div>

              {/* Purchase Pattern Analysis Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2.5">
                  <ShoppingCart className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Purchase Pattern Analysis
                    </h4>
                    <p className="text-xs text-gray-500">
                      Track buying habits easily
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 rounded-b-lg">
          <Button
            onClick={handleInstall}
            className="bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium flex-1"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Install Insights
          </Button>
          <button
            onClick={handleMaybeLater}
            className="text-gray-600 hover:text-gray-900 text-xs font-medium transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
            Maybe Later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
